export type BcraReportStatus = 'not-started' | 'incomplete' | 'pending' | 'ready' | 'ineligible' | 'error';

/** Answers the Gail model isn't valid for, highest priority first. */
export type BcraIneligibleReason = 'brca' | 'in-situ' | 'under-35' | 'hodgkin';

export type BreastDensity = 'Dense' | 'Not Dense' | 'Unknown';

export type BcraRiskKind = 'five-year' | 'lifetime';

export interface BcraChartScale {
  max: number;
  ticks: number[];
  /** High-risk threshold emphasised on the chart, if the chart has one. */
  highlightedTick: number | null;
}

export interface BcraRiskEstimate {
  kind: BcraRiskKind;
  /** Gail model absolute risk (summary[0].AbsRisk), in percent. */
  risk: number;
  /** Model's U.S. average for the same age and race/ethnicity (summary[0].AbsRisk_Avg), in percent. */
  averageRisk: number;
  scale: BcraChartScale;
}

export interface BcraResult {
  /** The 5-year estimate first, then the lifetime one when the calculation provides it. */
  estimates: BcraRiskEstimate[];
  /** From requestData.personalHealthHistory.breastDensity. */
  breastDensity: BreastDensity;
  /** When the questionnaire was submitted (requestData.createdAt). */
  answeredAt: Date | null;
}

export interface BcraReport {
  status: BcraReportStatus;
  /** Only present once the report is ready. */
  result: BcraResult | null;
  /** Only present when the status is 'ineligible'. */
  ineligibleReason?: BcraIneligibleReason;
}

export interface BcraIneligibleContent {
  /** Bold lead-in of the notice. */
  lead: string;
  body: string;
}

export interface BcraStatusContent {
  title: string;
  body: string;
  showReviewSurvey: boolean;
  showReportIssue: boolean;
  /** Call-to-action button below the card, if any; `quizPath` is relative to the breast health quiz. */
  cta: { label: string; quizPath: string } | null;
}

/** Shared by the report page and the PDF. */
export const RISK_COPY: Record<BcraRiskKind, { period: string; label: string; caption: string }> = {
  'five-year': {
    period: 'over the next 5 years',
    label: 'Your 5-Year Risk',
    caption: 'Estimated 5-year invasive breast cancer risk',
  },
  'lifetime': {
    period: 'up to age 90',
    label: 'Your Lifetime Risk',
    caption: 'Estimated invasive breast cancer risk up to age 90',
  },
};

/** 5-year risks are usually a few percent, so that chart gets a finer axis than the lifetime one. */
export function riskChartScale(kind: BcraRiskKind, values: number[]): BcraChartScale {
  // Lifetime risk of 20% or more is the usual high-risk threshold; steps of 10 and 20 both keep it on a tick
  return kind === 'lifetime'
    ? { ...niceScale(values, 50, [10, 20]), highlightedTick: 20 }
    : { ...niceScale(values, 5, [1, 2, 5, 10, 20]), highlightedTick: null };
}

/** At least `minMax` wide, grown to fit the largest value, using the smallest step that keeps at most 6 intervals. */
function niceScale(values: number[], minMax: number, steps: number[]): { max: number; ticks: number[] } {
  const peak = Math.min(Math.max(minMax, ...values), 100);
  const step = steps.find((s) => Math.ceil(peak / s) <= 6) ?? steps[steps.length - 1];
  const max = Math.ceil(peak / step) * step;
  return { max, ticks: Array.from({ length: max / step + 1 }, (_, i) => i * step) };
}

export function hasLifetimeEstimate(result: BcraResult): boolean {
  return result.estimates.some((e) => e.kind === 'lifetime');
}

/** Opening paragraph of the report. */
export function reportIntro(result: BcraResult): string {
  return hasLifetimeEstimate(result)
    ? 'Your result uses the Gail model to estimate your risk of invasive breast cancer over the next 5 years and up to age 90 (your lifetime risk). It can help you and your healthcare professional discuss your breast health and next steps.'
    : 'Your result uses the Gail model to estimate your risk of invasive breast cancer over the next 5 years. This is a 5-year risk assessment, not a lifetime risk assessment. It can help you and your healthcare professional discuss your breast health and next steps.';
}

/** First paragraph of "About this estimate". */
export function reportAbout(result: BcraResult): string {
  return hasLifetimeEstimate(result)
    ? 'This report uses the Gail model to estimate invasive breast cancer risk over the next 5 years and up to age 90. It considers age, race/ethnicity, menstrual and birth history, breast biopsies, and breast cancer in your mother, sisters or daughters.'
    : 'This report uses the Gail model to estimate invasive breast cancer risk over the next 5 years. It does not show your lifetime risk. It considers age, race/ethnicity, menstrual and birth history, breast biopsies, and breast cancer in your mother, sisters or daughters.';
}

export function estimateSummary(estimate: BcraRiskEstimate): string {
  return `Your estimated risk of invasive breast cancer ${RISK_COPY[estimate.kind].period} is ${formatPercent(estimate.risk)}%, compared with the model’s U.S. average of ${formatPercent(estimate.averageRisk)}% for your age and race/ethnicity.`;
}

function formatPercent(value: number): string {
  return String(Math.round(value * 10) / 10);
}

/** Shared by the report page and the PDF. */
export const SCREENING_FACTORS: readonly string[] = [
  'Your age',
  'Your breast density',
  'Previous breast conditions or biopsies',
  'Genetic test results',
  'A history of radiation therapy to the chest',
  'Changes in your personal or family cancer history',
  'Previous mammogram or imaging results',
];

/** Shared by the report page and the PDF. */
export const GAIL_LIMITATIONS: readonly string[] = [
  'Are younger than 35 or older than 85',
  'Have previously been diagnosed with invasive breast cancer, ductal carcinoma in situ (DCIS) or lobular carcinoma in situ (LCIS)',
  'Have a known BRCA1 or BRCA2 mutation or another hereditary condition associated with increased breast cancer risk',
  'Have received radiation therapy to the chest for Hodgkin lymphoma',
];

export type FaqBlock =
  | { type: 'text'; text: string }
  | { type: 'link'; text: string; href: string };

export interface FaqItem {
  question: string;
  answer: FaqBlock[];
}
